import React, {useEffect, useState} from 'react';
import {
  Box,
  Heading,
  Select,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  List,
  ListItem,
} from '@chakra-ui/react';

import dayjs from 'dayjs';

import {api} from "../../api/userAPI";
import {PerformanceIncentiveTriggers} from "./PerformanceIncentiveTriggers";
import {CompensatoryDatesTable} from "./CompensatoryDatesTable";
import {UserManagementTable} from "./UserManagementTable";
type RoleType = 'viewer' | 'editor' | 'admin';

type Roles = {
  [user: string]: {
    performanceRole?: RoleType;
    isAdmin?: boolean;
    canImpersonate?: boolean;
  };
};

type User = {
  id: string;
  name: string;
  role: string;
};

// const mockUsers: User[] = Array.from({ length: 700 }, (_, i) => ({
//   id: `user${i + 1}`,
//   name: `User ${i + 1}`,
//   role: "viewer"
// }));


const AdminPanel: React.FC = () => {
  const [roles, setRoles] = useState<Roles>({});

  const [preview, setPreview] = useState<Roles>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleType | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    api.getActiveManagementUsers().then(value => {
      const data: any[] = value.data;
      const userList: User[] = data.map(value1 => {
        return {id: value1.email, name: value1.email, role: value1.access}
      })
      setUsers(userList)
      let roles: Roles = {};
      userList.filter(user =>user.role !== null ).forEach(user => {
            roles[user.id] = {
              performanceRole: user.role as RoleType,
              isAdmin: user.role === 'admin',
              canImpersonate: user.role === 'admin' || user.role === 'viewer' || user.role === "editor",
            };

          }
      );
      setRoles(roles)

    })
  }, [refreshIndex]);



const handleRoleChange = (user: string| null, role: RoleType) => {
  if (!user) return
  setPreview((prevPreview) => {
    const updatedRoles = {
      ...prevPreview,
      [user]: {
        performanceRole: role
      },
    };
    return updatedRoles;
  });
};


  const finalizePreview = () => {
    //TODO  Call API
  const roleList = Object.entries(preview).map(([email, roleData]) => ({
    email,
    role: roleData.performanceRole ?? null, // or leave undefined if you prefer
  }));
    api.addUserRoles({"roles": roleList}).then(r =>handleRefresh())
    setPreview({});
  };

  const clearPreview = () => {
    setPreview({});
  };
  const handleRefresh = () => {
    setRefreshIndex(prev => prev + 1); // Trigger re-fetch
  };

  const revokePermission = (userId: string) => {
    api.revokeUserAccess(userId).then(value => handleRefresh())
  };

  const filteredRoles = Object.entries(roles).filter(([userId, role]) => {
    const userName = users.find((user) => user.id === userId)?.name || "";
    return userName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Box p={5} borderWidth={1} borderRadius="md" shadow="md">
      <Heading textAlign="center" mb={5}>Admin Panel</Heading>
      <Flex justify="center" wrap="wrap" gap={10}>
        {/* Performance Incentive Roles */}
        <Box borderWidth={1} borderRadius="md" p={3} shadow="sm" width="sm">
          <Heading size="sm" mb={3}>Performance Incentive Roles</Heading>
          <FormControl>
            <FormLabel>Select User</FormLabel>
            <Select placeholder="Select User"
                    onChange={(e) => setSelectedUser(e.target.value)}>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </Select>
            <FormLabel mt={3}>Assign Role</FormLabel>
            <Select
              placeholder="Assign Role"
            onChange={(e) => {
              const role = e.target.value as RoleType;
              setSelectedRole(role);
              handleRoleChange(selectedUser, role );
            }}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </Select>
          </FormControl>
          <List mt={3} spacing={2}>
            {Object.entries(preview).map(([userId, role]) => (
              role.performanceRole && (
                <ListItem key={userId}>{userId} - {role.performanceRole}</ListItem>
              )
            ))}
          </List>
          <Flex mt={3} gap={3}>
            <Button colorScheme="teal" size="sm" onClick={finalizePreview}>
              Add to Final List
            </Button>
            <Button colorScheme="red" size="sm" onClick={clearPreview}>
              Clear Preview
            </Button>
          </Flex>
        </Box>


      </Flex>

      {/* Existing Data Table */}
      <Box mt={10} borderWidth={1} borderRadius="md" p={3} shadow="sm" maxHeight="300px" overflowY="auto">
        <Heading size="sm" mb={3}>Current Assignments</Heading>
        <Input
          placeholder="Search by user name"
          mb={3}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Table variant="striped" size="sm">
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Performance Role</Th>
              <Th>Is Admin</Th>
              <Th>Can Impersonate</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRoles.map(([userId, role]) => (
              <Tr key={userId}>
                <Td>{userId}</Td>
                <Td>{role.performanceRole || "-"}</Td>
                <Td>{role.isAdmin ? "Yes" : "No"}</Td>
                <Td>{role.canImpersonate ? "Yes" : "No"}</Td>
                <Td>
                  {role.performanceRole && (
                    <Button size="xs" colorScheme="red" onClick={() => revokePermission(userId)}>Revoke</Button>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {/* User Management Table */}
      <UserManagementTable />

    <Flex flexGrow={1} gap={10}>
      {/* Performance Incentive Date Selection */}
      <PerformanceIncentiveTriggers/>
      {/* Compensatory Dates Table */}
      <CompensatoryDatesTable/>
    </Flex>
    </Box>
  );
};

export default AdminPanel;


